import * as React from 'react'

import { i18n, useCustomer, useSiteConfig } from '@thg-commerce/enterprise-core'
import { FulfilmentMethod } from '@thg-commerce/enterprise-network/src/generated/graphql'
import { useTheme } from '@thg-commerce/enterprise-theme'
import {
  ImageOrientation,
  ShopTheRoom,
} from '@thg-commerce/enterprise-widget-shop-the-room'
import { ColourChoice } from '@thg-commerce/gravity-elements/Swatch'
import {
  SwatchShape,
  SwatchSize,
} from '@thg-commerce/gravity-elements/Swatch/types'
import { PictureProps } from '@thg-commerce/gravity-system'

export interface ProductProps {
  sku: string
  colourSwatches?: ColourChoice[]
  externalIdentifier?: string
  eligibleForFulfilmentMethods?: FulfilmentMethod
  hoverImage?: {
    urls: {
      largeProduct: string
      thumbnail?: string
    }
    altText: string
    isAmp: boolean
    lazy?: boolean
  }
  hoverPicture?: PictureProps
  image: {
    urls: {
      thumbnail?: string
      largeProduct?: string
      zoom?: string
    }
    alt: string
    lazy?: boolean
  }
  inStock: boolean
  isCheckStock: boolean
  isOrderInStore: boolean
  icons?: {
    homeDelivery: boolean
    storeAvailable: boolean
    storeDelivery: boolean
  }
  marketedSpecialOffer?: {
    titleText?: string | null
    descriptionText?: string | null
    landingPageLink?: {
      text: string
      url: string
      openExternally?: boolean | null
      noFollow?: boolean | null
    } | null
  }
  picture?: PictureProps
  reviews?: {
    averageScore: number
    total: number
  } | null
  title: string
  url: string
}

interface ShopTheRoomWidgetRendererProps {
  description: string
  displayAsGrid: boolean
  displayQuickbuy: boolean
  imageAlt: string
  imageLarge: string
  imageMedium: string
  imageSmall: string
  orientation: ImageOrientation
  quickBuyLabel: string
  productList: { productBlocks: ProductProps[] }
  shopAllLabel: string
  shopAllLink: string
  title: string
  widgetIndex: number
  picture?: PictureProps
}

export const ShopTheRoomWidgetRenderer = (
  props: ShopTheRoomWidgetRendererProps,
) => {
  const theme = useTheme()
  const { hideProductListProductBlockButton } = useSiteConfig()
  const customer = useCustomer()

  const PRODUCTS_INITIALLY_VISIBLE =
    props.orientation === ImageOrientation.LANDSCAPE ? 5 : 3

  const i18nText = {
    closeAriaLabel: i18n('general.modal.close.button.arialabel'),
    closeLabel: i18n('general.modal.close.button.label'),
    freeGiftMessage: i18n('product.marketedspecialoffer.freegift.text'),
    rrpText: i18n('product.price.rrp.text'),
    swatchI18nText: {
      swatchTooltipUnavailable: i18n(
        'product.productoptions.swatch.tooltip.unavailable.text',
      ),
      swatchTooltipCloseButton: i18n(
        'product.productoptions.swatch.tooltip.close.text',
      ),
    },
    buyAriaLabel: i18n('general.productbuynow.label'),
    quickBuyText: i18n('general.product.quickbuy.text'),
    soldOutText: i18n('general.productsoldout.text'),
    reviewsStarsLabel: i18n('reviews.stars.label'),
    reviewsLabel: i18n('reviews.reviews.label'),
  }

  const componentsGrid =
    props.orientation === ImageOrientation.PORTRAIT && !props.displayAsGrid
      ? {
          title: {
            colStart: [1, 1, 7, 7],
            colSpan: [12, 12, 6, 6],
            rowStart: [1, 1, 1, 1],
            rowSpan: [1, 1, 1, 1],
          },
          image: {
            colStart: [1, 1, 1, 1],
            colSpan: [12, 12, 6, 6],
            rowStart: [2, 2, 1, 1],
            rowSpan: [3, 3, 8, 8],
          },
          carousel: {
            colStart: [1, 1, 7, 7],
            colSpan: [12, 12, 6, 6],
            rowStart: [5, 5, 2, 2],
            rowSpan: [3, 3, 7, 7],
          },
          button: {
            colStart: [1, 1, 1, 1],
            colSpan: [12, 12, 12, 12],
            rowStart: [8, 8, 9, 9],
            rowSpan: [1, 1, 1, 1],
          },
        }
      : {
          title: {
            colStart: [1, 1, 1, 1],
            colSpan: [12, 12, 12, 12],
            rowStart: [1, 1, 1, 1],
            rowSpan: [1, 1, 1, 1],
          },
          image: {
            colStart: [1, 1, 1, 1],
            colSpan: [12, 12, 12, 12],
            rowStart: [2, 2, 2, 2],
            rowSpan: [3, 3, 3, 3],
          },
          carousel: {
            colStart: [1, 1, 1, 1],
            colSpan: [12, 12, 12, 12],
            rowStart: [5, 5, 5, 5],
            rowSpan: [3, 3, 3, 3],
          },
          button: {
            colStart: [1, 1, 1, 1],
            colSpan: [12, 12, 12, 12],
            rowStart: [8, 8, 8, 8],
            rowSpan: [1, 1, 1, 1],
          },
        }

  if (!props.productList) {
    return null
  }

  const screenReaderReviewText = ({ score, total }) =>
    `${score} ${i18nText.reviewsStarsLabel} ${total} ${i18nText.reviewsLabel}`

  const wishListButtonStyle = theme.widget.productList.wishlistButton?.style

  const shopTheRoomProps = {
    componentsGrid,
    wishListButtonStyle,
    wishlist: customer?.wishlist,
    breakpoints: theme.breakpointUtils.map,
    title: props.title,
    description: props.description,
    image: {
      urls: {
        small: props.imageSmall,
        medium: props.imageMedium,
        large: props.imageLarge,
      },
      alt: props.imageAlt,
    },
    picture: props.picture,
    orientation: props.orientation,
    displayQuickBuyButton: props.displayQuickbuy,
    widgetIndex: props.widgetIndex,
    shopAll: { link: props.shopAllLink, label: props.shopAllLabel },
    displayAsGrid: props.displayAsGrid,
    indicatorPadding: theme.widget.shopTheRoom.carousel.indicatorPadding,
    productBlocks: props.productList.productBlocks.map((product, index) => {
      return {
        ...product,
        customStyling: {
          containerHeight:
            props.displayQuickbuy && !hideProductListProductBlockButton
              ? '100%'
              : 'auto',
        },
        title: { value: product.title },
        marketedSpecialOffer: product.marketedSpecialOffer
          ? {
              title: product.marketedSpecialOffer.titleText || '',
              description: product.marketedSpecialOffer.descriptionText || '',
              landingPageLink: {
                text: product.marketedSpecialOffer.landingPageLink?.text || '',
                url: product.marketedSpecialOffer.landingPageLink?.url || '',
                openExternally:
                  product.marketedSpecialOffer.landingPageLink
                    ?.openExternally || false,
                noFollow:
                  product.marketedSpecialOffer.landingPageLink?.noFollow ||
                  true,
                onlyDisplayOfferBadge: true,
              },
              i18nText: {
                closeI18nText: {
                  closeLabel: i18nText.closeLabel,
                  closeAriaLabel: i18nText.closeAriaLabel,
                },
                freeGiftMessage: i18nText.freeGiftMessage,
              },
              onlyDisplayOfferBadge: true,
            }
          : undefined,
        price: {
          ...(product as any).price,
          rrpText: i18nText.rrpText,
        },
        swatch: product.colourSwatches
          ? {
              shape: SwatchShape.CIRCLE,
              size: SwatchSize.Small,
              colours: product.colourSwatches,
              i18nText: {
                unavailableText:
                  i18nText.swatchI18nText.swatchTooltipUnavailable,
                closeButtonText:
                  i18nText.swatchI18nText.swatchTooltipCloseButton,
              },
            }
          : undefined,
        image: {
          ...product.image,
          lazy: index > PRODUCTS_INITIALLY_VISIBLE - 1,
          isAmp: false,
        },
        picture: {
          style: { width: '100%' },
          ...(product as any).picture,
          alt: product.title,
          lazy: index > PRODUCTS_INITIALLY_VISIBLE - 1,
        },
        review: product.reviews
          ? {
              starRating: product.reviews?.averageScore,
              numberOfReviews: product.reviews?.total,
              screenReaderOnlyText: screenReaderReviewText({
                score: product.reviews.averageScore,
                total: product.reviews.total,
              }),
            }
          : undefined,
        button: {
          title:
            !props.displayQuickbuy || product.inStock
              ? props.quickBuyLabel || i18nText.quickBuyText
              : i18nText.soldOutText,
          ariaLabel: i18nText.buyAriaLabel,
          quickBuyTitle: props.quickBuyLabel || i18nText.quickBuyText,
          productInStock: product.inStock,
          disabled: !product.inStock,
          buttonStyle:
            theme.widget.shopTheRoom?.productBlock?.components?.productButton,
        },
      }
    }),
  }

  return <ShopTheRoom {...shopTheRoomProps} />
}
